import {
  AdgroupFormData,
  CampaignFormData,
  InventoryFlow,
  AppEvents,
  EventStatus,
  AdgroupConfig,
} from '@tt4b/ttam-enums';
import {
  useStdAdgroupIs,
  useAdgroupAppEvents,
  useStdAdgroupOptimizationGoal,
  useStdAdgroupCbo,
} from '@tt4b/ttam-hooks';
import { computed, Ref } from '@vue/composition-api';

/** TiktokWarningTips  */
export function useTiktokWarningTipsPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { campaignData, adgroupData } = props;
  const { hasInventoryFlow } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  return {
    visible: computed(() => !hasInventoryFlow(InventoryFlow.TIKTOK)),
  };
}

/** AllEventsInactiveTips  */
export function useAllEventsInactiveTipsPropsVisible(props: {
  appEvents: Ref<AppEvents>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { appEvents, adgroupData, campaignData } = props;

  const { inAppEvents } = useAdgroupAppEvents({
    appEvents,
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isCreateNonFirstCBOAdgroup } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const isAllEventInactive = computed(() => {
    const events = inAppEvents.value.events || [];

    return !events.some(item => item.is_beta || Number(item.aeo_availability) === EventStatus.READY);
  });

  return {
    // cbo non-first adg 场景不出这个提示
    visible: !isCreateNonFirstCBOAdgroup && isAllEventInactive,
  };
}

/** ExternalActionSelect  */
export function useExternalActionSelectPropsDisabled(props: {
  adgroupConfig: Ref<AdgroupConfig>;
  type: 'deep-event' | 'in-event';
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupConfig, type, adgroupData, campaignData } = props;

  const { isCreateNonFirstCBOAdgroup } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const disabled = computed(() => {
    if (type === 'deep-event') {
      return adgroupConfig.value?.disableDeepEvent;
    } else {
      return isCreateNonFirstCBOAdgroup.value || adgroupConfig.value.disableInAppEvent;
    }
  });

  return {
    disabled,
  };
}

export function useExternalActionSelectPropsOptions(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
  type: 'deep-event' | 'in-event';
  externalAction?: Ref<number | ''>;
}) {
  const { type, adgroupData, appEvents, campaignData, externalAction } = props;

  const { inAppEvents, deepAppEvents } = useAdgroupAppEvents({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
    appEvents,
  });

  const { isCreateNonFirstCBOAdgroup } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const options = computed(() => {
    if (type === 'deep-event') {
      return deepAppEvents.value;
    } else {
      const inAppEventsList = inAppEvents.value?.events;
      if (
        !isCreateNonFirstCBOAdgroup.value ||
        inAppEventsList.some(item => item.external_action === externalAction.value)
      ) {
        return inAppEvents.value?.events;
      }
      return [
        {
          external_action: externalAction.value,
          external_action_i18nkey: ' ',
        },
        ...inAppEvents?.value?.events,
      ];
    }
  });

  return {
    options,
  };
}

export function usePropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
}) {
  const { adgroupData, appEvents, campaignData } = props;

  const { inAppEvents, supportDeepAppEvents } = useAdgroupAppEvents({
    adgroupFormData: adgroupData,
    appEvents,
    campaignFormData: campaignData,
  });
  const { isOptGoalInAppEvent } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });
  const inAppEventVisible = computed(() => isOptGoalInAppEvent.value && inAppEvents.value.events.length > 0);
  const deepAppEventVisible = computed(() => supportDeepAppEvents.value);
  return {
    visible: computed(() => inAppEventVisible.value || deepAppEventVisible.value),
    inAppEventVisible,
    deepAppEventVisible,
  };
}

export function useExternalActionSelectPropsisCboAeo(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;

  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isOptGoalInAppEvent } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });

  return {
    isCboAeo: computed(() => isCBO.value && isOptGoalInAppEvent.value),
  };
}

export function useEANotSupportTipsPropsVisible(props: {
  appEvents: Ref<AppEvents>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  externalAction: Ref<number | ''>;
}) {
  const { appEvents, adgroupData, campaignData, externalAction } = props;
  const { inAppEvents } = useAdgroupAppEvents({
    appEvents,
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCreateNonFirstCBOAdgroup } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const eaNotSupportTipsVisible = computed(() => {
    const curEvent = inAppEvents?.value?.events.find(item => item.external_action === externalAction.value);
    return isCreateNonFirstCBOAdgroup.value && !(curEvent?.is_beta || curEvent?.aeo_availability === EventStatus.READY);
  });

  return {
    visible: eaNotSupportTipsVisible,
  };
}
